import { useMemo, useState } from 'react'
import { X } from 'react-feather'
import AlertTriangle from 'public/images/billing/alert-triangle.svg'
import { useRouter } from 'next/router'
import RipplePulse from 'src/components/common/loader-components/RipplePulse'
import { useOrganisationContext, OverDueStatus } from 'src/lib/contexts/OrganisationContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getDueDateStatus } from 'src/lib/utils/OrganisationHelpers'
import { classNames } from 'src/lib/utils/helpers'

interface OverDueInvoicesBannerTermsAndConditionsProps {
  variant?: OverDueStatus
}

const dismissButtonClasses = (variant: OverDueStatus): string => {
  const commonColors = 'text-black/60'
  const classes = {
    INVOICES_GENERATED: `${commonColors}`,
    REMINDER_STATE: `${commonColors}`,
    OVERDUE: `text-white`,
    NONE: `${commonColors}`
  }
  return classes[variant] || commonColors
}

const termsConditionsClasses = (variant: OverDueStatus): string => {
  const commonColors = 'text-black/60 border-b-1 border-black/60'
  const classes = {
    INVOICES_GENERATED: `${commonColors}`,
    REMINDER_STATE: `${commonColors} text-sm`,
    OVERDUE: `text-white border-b-1 border-white`,
    NONE: `${commonColors}`
  }
  return classes[variant] || commonColors
}

const OverDueInvoicesBanner = () => {
  const {
    getters: {
      status,
      organisationData: { status: orgStatus }
    }
  } = useOrganisationContext()

  if (orgStatus === 'DISABLED') {
    return <></>
  }

  switch (status) {
    case 'INVOICES_GENERATED':
      return <FirstReminderBanner />
    case 'REMINDER_STATE':
      return <SecondReminderBanner />
    case 'OVERDUE':
      return <FinalNoticeBanner />
    default:
      return <></>
  }
}

export default OverDueInvoicesBanner

const FirstReminderBanner = () => {
  const {
    getters: { billingPopUp, isOverDueInvoicesBannerOpen, bannerText }
  } = useOrganisationContext()

  return isOverDueInvoicesBannerOpen && billingPopUp.visible ? (
    <div className='px-1.5 pb-1.5'>
      <div className='bg-sky-100 w-full flex items-center justify-between p-[10px] text-sm rounded-b-xl border-1 border-gray-200'>
        <div className='flex items-center w-full justify-center'>
          <p className='text-black/60 text-center' dangerouslySetInnerHTML={{ __html: bannerText }} />
          <OverDueInvoicesBannerTermsAndConditions variant='INVOICES_GENERATED' />
        </div>
        <OverDueInvoicesBannerDismissButton variant='INVOICES_GENERATED' />
      </div>
    </div>
  ) : (
    <></>
  )
}

const SecondReminderBanner = () => {
  const {
    getters: { isOverDueInvoicesBannerOpen, billingPopUp, bannerText }
  } = useOrganisationContext()

  return billingPopUp.visible && isOverDueInvoicesBannerOpen ? (
    <div className='px-1.5 pb-1.5'>
      <div className='bg-amber-100 w-full flex items-center justify-between p-[10px] text-sm rounded-xl border-1 border-gray-200'>
        <p className=' flex-grow text-center text-black/60' dangerouslySetInnerHTML={{ __html: bannerText }} />
        <OverDueInvoicesBannerTermsAndConditions variant='REMINDER_STATE' />
        <OverDueInvoicesBannerDismissButton variant='REMINDER_STATE' />
      </div>
    </div>
  ) : (
    <></>
  )
}

const FinalNoticeBanner = () => {
  const router = useRouter()
  const {
    getters: { isOverDueInvoicesBannerOpen, billingPopUp, bannerText },
    actions: { setPayNowClicked }
  } = useOrganisationContext()

  const { t } = useLocale()

  const getOverDueInvoiceText = () => {
    if (billingPopUp.overDueSince) {
      return t('billing.invoices_overdue_by', {
        numberOfOverDueInvoices: billingPopUp.numberOfOverDueInvoices,
        days: billingPopUp.overDueSince
      })
    } else {
      return t('billing.invoices_overdue', {
        numberOfOverDueInvoices: billingPopUp.numberOfOverDueInvoices
      })
    }
  }

  return billingPopUp.visible && isOverDueInvoicesBannerOpen ? (
    <div className='w-full flex flex-col items-center justify-between p-5 pb-3 rounded-b-2xl text-sm bg-carbon gap-3 border-t-2 border-coal-light'>
      <div className='flex items-center gap-2 justify-between w-full bg-[#FAFAFA1A] p-3 rounded-xl'>
        <div className='flex items-center gap-8 '>
          <div className='flex flex-col gap-2 text-sm'>
            <p className='text-carbon text-medium text-white'>{t('billing.overdue_amount')}</p>
            <p className='text-white font-semibold '>₹{billingPopUp.overDueAmount}</p>
          </div>
          <div className='flex items-center gap-2 border border-red-dark rounded-2xl px-3 py-2 bg-white text-red-dark'>
            <RipplePulse color='bg-red-dark' />
            <p>{getOverDueInvoiceText()}</p>
          </div>
        </div>
        <div>
          <button
            className='bg-white text-coal-dark px-3 py-2 rounded-lg'
            onClick={() => {
              router.push('/billing')
              setPayNowClicked(true)
            }}
          >
            {t('billing.pay_now')}
          </button>
        </div>
      </div>
      <div className='flex items-center justify-between w-full'>
        <div className='flex items-center gap-2 justify-center w-[90%]'>
          <AlertTriangle />
          <p className='text-white text-base font-medium' dangerouslySetInnerHTML={{ __html: bannerText }} />
        </div>
        <div className='flex items-center gap-2'>
          <OverDueInvoicesBannerTermsAndConditions variant='OVERDUE' />
          <OverDueInvoicesBannerDismissButton variant='OVERDUE' />
        </div>
      </div>
    </div>
  ) : (
    <></>
  )
}

const OverDueInvoicesBannerTermsAndConditions: React.FC<OverDueInvoicesBannerTermsAndConditionsProps> = ({
  variant = 'OVERDUE'
}) => {
  const { t } = useLocale()

  return (
    <a
      className={classNames('text-sm ml-4 cursor-pointer', termsConditionsClasses(variant))}
      href='https://intercom.help/shopflo-a9de00772be8/en/articles/10665779-shopflo-payment-terms-updated'
      target='_blank'
      rel='noreferrer'
    >
      {t('billing.view_t_c')}
    </a>
  )
}

const OverDueInvoicesBannerDismissButton = ({ variant = 'OVERDUE' }: { variant?: OverDueStatus }) => {
  const {
    getters: { billingPopUp },
    actions: { setOverDueInvoicesBannerOpen }
  } = useOrganisationContext()
  const { t } = useLocale()

  return (
    <div className='flex items-center gap-6'>
      {billingPopUp.dismissible && (
        <button className={dismissButtonClasses(variant)} onClick={() => setOverDueInvoicesBannerOpen(false)}>
          <X size={16} />
        </button>
      )}
    </div>
  )
}
